import React, { useEffect } from 'react';
import { FileText, MapPin, Users, Calendar, DollarSign } from 'lucide-react';
import { FormButton } from '../common/Form';
import { TripFormData } from './TripWizardModal';

interface Step3Props {
  data: TripFormData;
  onChange: (data: Partial<TripFormData>) => void;
  onNext: () => void;
  onPrev: () => void;
  isLoading: boolean;
  isLastStep?: boolean;
}

export function Step3Summary({ data, onChange, onNext, onPrev, isLoading, isLastStep = false }: Step3Props) {
  const DAILY_RATE = 4.15;
  const MINIMUM_AMOUNT = 41.50;

  useEffect(() => {
    // Calculate pricing when component mounts or data changes
    const calculatePricing = () => {
      if (!data.total_days || !data.participants.length) return;

      const payingParticipants = data.participants.filter(p => !p.exemption_reason_id);
      const exemptParticipants = data.participants.filter(p => p.exemption_reason_id);

      // Calculate amount per paying participant
      const amountPerParticipant = Math.max(data.total_days * DAILY_RATE, MINIMUM_AMOUNT);
      const total_amount = payingParticipants.length * amountPerParticipant;

      onChange({
        total_amount,
        exemptions_applied: exemptParticipants.length,
      });
    };

    calculatePricing();
  }, [data.total_days, data.participants, onChange]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const payingParticipants = data.participants.filter(p => !p.exemption_reason_id);
  const exemptParticipants = data.participants.filter(p => p.exemption_reason_id);
  const amountPerParticipant = data.total_days ? Math.max(data.total_days * DAILY_RATE, MINIMUM_AMOUNT) : 0;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Resumo da Viagem</h3>
        <p className="text-gray-600 mt-2">
          {isLastStep 
            ? 'Confira os detalhes antes de confirmar sua viagem' 
            : 'Confira os detalhes antes de prosseguir para o pagamento'
          }
        </p>
      </div>

      {/* Trip Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <MapPin className="h-5 w-5 text-blue-600 mr-2" />
          Detalhes da Viagem
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Hotel/Pousada</p>
            <p className="font-medium text-gray-900">{data.hotel_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Duração</p>
            <p className="font-medium text-gray-900">
              {data.total_days} {data.total_days === 1 ? 'dia' : 'dias'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Check-in</p>
            <p className="font-medium text-gray-900">{formatDate(data.start_date)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Check-out</p>
            <p className="font-medium text-gray-900">{formatDate(data.end_date)}</p>
          </div>
        </div>
      </div>

      {/* Participants */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 text-blue-600 mr-2" />
          Participantes ({data.participants.length})
        </h4>

        <div className="space-y-3">
          {data.participants.map((participant, index) => {
            const age = calculateAge(participant.date_of_birth);
            const isExempt = !!participant.exemption_reason_id;

            return (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-gray-900">{participant.name}</p>
                    {isExempt && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Isento
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {age} anos • CPF: {participant.document_number.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                  </p>
                  {isExempt && (
                    <p className="text-xs text-green-600">
                      {age < 12 ? 'Menor de 12 anos' : age >= 60 ? 'Maior de 60 anos' : 'Motivo de isenção aplicado'}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    {isExempt ? 'R$ 0,00' : formatCurrency(amountPerParticipant)}
                  </p>
                  {!isExempt && (
                    <p className="text-xs text-gray-500">
                      {data.total_days}x {formatCurrency(DAILY_RATE)}
                      {amountPerParticipant > (data.total_days || 0) * DAILY_RATE && (
                        <span className="block">Mín. {formatCurrency(MINIMUM_AMOUNT)}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pricing Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-4 flex items-center">
          <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
          Resumo Financeiro
        </h4>

        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-blue-700">Participantes pagantes:</span>
            <span className="text-blue-900 font-medium">{payingParticipants.length}</span>
          </div>
          
          {exemptParticipants.length > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-blue-700">Participantes isentos:</span>
              <span className="text-blue-900 font-medium">{exemptParticipants.length}</span>
            </div>
          )}

          <div className="flex justify-between text-sm">
            <span className="text-blue-700">Taxa por participante:</span>
            <span className="text-blue-900 font-medium">{formatCurrency(amountPerParticipant)}</span>
          </div>

          <div className="flex justify-between text-sm border-t border-blue-200 pt-3">
            <span className="text-blue-700">Taxa diária (R$ {DAILY_RATE.toFixed(2)}):</span>
            <span className="text-blue-900 font-medium">
              {data.total_days} {data.total_days === 1 ? 'dia' : 'dias'}
            </span>
          </div>

          <div className="flex justify-between items-center text-lg font-semibold border-t border-blue-200 pt-3">
            <span className="text-blue-900">Total a pagar:</span>
            <span className="text-blue-900">{formatCurrency(data.total_amount || 0)}</span>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h5 className="font-medium text-yellow-900 mb-2">📋 Informações Importantes:</h5>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• O pagamento deve ser realizado para confirmar a viagem</li>
          <li>• Após a confirmação, você receberá os QR codes individuais</li>
          <li>• Os QR codes devem ser apresentados na chegada ao destino</li>
          <li>• Mantenha os comprovantes de pagamento para consulta</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-6">
        <FormButton
          type="button"
          variant="ghost"
          onClick={onPrev}
          disabled={isLoading}
        >
          Voltar
        </FormButton>
        
        <FormButton
          type="button"
          variant="primary"
          onClick={onNext}
          disabled={isLoading}
          className="min-w-[120px]"
        >
          {isLastStep ? 'Confirmar Viagem' : 'Prosseguir para Pagamento'}
        </FormButton>
      </div>
    </div>
  );
}

export default Step3Summary;
